import { NavLink } from 'react-router-dom'
import {
  LayoutDashboard,
  FolderOpen,
  BookOpen,
  Settings,
  Network,
  Shield,
  Database,
  Link as LinkIcon,
  HelpCircle,
  ChevronLeft,
  Beaker,
  Building2,
  GitBranch,
  Target,
  TrendingUp,
  GraduationCap,
  Award,
  BookMarked,
  Calendar
} from 'lucide-react'
import { useAuth } from '../contexts/AuthContext'

const navigation = {
  principal: [
    { name: 'Accueil', href: '/dashboard', icon: LayoutDashboard },
    { name: 'Projets', href: '/projects', icon: FolderOpen },
    { name: 'Métier', href: '/metier', icon: Database, description: 'Base de données compétences' },
    { name: 'Chaîne', href: '/chaine', icon: LinkIcon, description: 'Import référentiels' },
    { name: 'Navette', href: '/navette', icon: GitBranch, description: 'Fusion & matching' },
    { name: 'Trame', href: '/trame', icon: Network, description: 'Visualisation de données' },
    { name: 'Aiguille', href: '/aiguille', icon: Target, description: 'Recommandations' },
    { name: 'Tapis', href: '/tapis', icon: TrendingUp, description: 'Plan de montée en compétences' },
    { name: 'Écheveau', href: '/echeveau', icon: Award, description: 'VAE & mobilité' },
    { name: 'Fusette', href: '/fusette', icon: BookMarked, description: 'Modules LMS' },
    { name: 'Ourdissage', href: '/ourdissage', icon: Calendar, description: 'Emploi du temps' },
  ],
  settings: [
    { name: 'Import/Export', href: '/import-export', icon: Database, roles: ['responsable', 'super_admin'] },
    { name: 'Audit & Logs', href: '/audit', icon: Shield, roles: ['responsable', 'super_admin'] },
    { name: 'Test France Travail', href: '/france-travail-test', icon: Beaker, roles: ['responsable', 'super_admin'] },
    { name: 'Test QuiForme', href: '/quiforme-test', icon: Building2, roles: ['responsable', 'super_admin'] },
    { name: 'Paramètres', href: '/settings', icon: Settings, roles: ['responsable', 'super_admin'] },
  ]
}

export default function Sidebar({ open, collapsed, onToggle, onClose }) {
  const { user, hasRole } = useAuth()

  const filterNavigation = (items) => {
    return items.filter(item => {
      if (item.roles) {
        return hasRole(item.roles)
      }
      return true
    })
  }

  return (
    <>
      {/* Sidebar Desktop */}
      <aside className={`fixed left-0 top-0 h-screen bg-white/80 backdrop-blur-xl border-r border-gray-100 z-50 transition-all duration-300 ${
        collapsed ? 'w-20' : 'w-64'
      } hidden lg:flex flex-col shadow-sm`}>

        {/* Header avec Logo */}
        <div className="h-16 flex items-center justify-between px-4 border-b border-gray-100">
          <div className="flex items-center gap-3 overflow-hidden">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary-500 to-secondary-500 flex items-center justify-center text-white font-heading font-bold text-lg shadow-md flex-shrink-0">
              O
            </div>
            {!collapsed && (
              <span className="font-heading font-semibold text-gray-900 whitespace-nowrap">Orbloom</span>
            )}
          </div>
          <button
            onClick={onToggle}
            className={`p-2 rounded-lg hover:bg-gray-100 transition-colors ${collapsed ? 'mx-auto' : ''}`}
          >
            <ChevronLeft className={`w-5 h-5 text-gray-600 transition-transform ${collapsed ? 'rotate-180' : ''}`} />
          </button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto py-6 px-3">
          {/* Section Principale */}
          <div className="space-y-1">
            {!collapsed && (
              <div className="px-3 mb-2">
                <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Principal</span>
              </div>
            )}
            {filterNavigation(navigation.principal).map((item) => (
              <NavLink
                key={item.name}
                to={item.href}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all ${
                    isActive
                      ? 'bg-primary-50 text-primary-600 font-medium shadow-sm'
                      : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                  } ${collapsed ? 'justify-center' : ''}`
                }
                onClick={() => window.innerWidth < 1024 && onClose()}
                title={collapsed ? item.name : item.description}
              >
                <item.icon className="w-5 h-5 flex-shrink-0" />
                {!collapsed && <span className="text-sm">{item.name}</span>}
              </NavLink>
            ))}
          </div>
        </nav>

        {/* Footer avec Paramètres (Admin) et Support */}
        <div className="p-3 border-t border-gray-100 space-y-2">
          {/* Bouton Paramètres (visible uniquement pour les admins) */}
          {hasRole(['responsable', 'super_admin']) && (
            <div className="relative group">
              <NavLink
                to="/settings"
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors ${
                    isActive
                      ? 'bg-gray-100 text-gray-900 font-medium'
                      : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                  } ${collapsed ? 'justify-center' : ''}`
                }
              >
                <Settings className="w-5 h-5 flex-shrink-0" />
                {!collapsed && <span className="text-sm">Paramètres</span>}
              </NavLink>

              {/* Dropdown menu pour les paramètres (visible au survol) */}
              {!collapsed && (
                <div className="hidden group-hover:block absolute bottom-full left-0 mb-2 w-64 bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-50">
                  <div className="px-3 py-2 text-xs font-semibold text-gray-400 uppercase tracking-wider border-b border-gray-100 mb-2">
                    Administration
                  </div>
                  {filterNavigation(navigation.settings).map((item) => (
                    <NavLink
                      key={item.name}
                      to={item.href}
                      className={({ isActive }) =>
                        `flex items-center gap-3 px-3 py-2 text-sm transition-colors ${
                          isActive
                            ? 'bg-primary-50 text-primary-600 font-medium'
                            : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                        }`
                      }
                      onClick={() => window.innerWidth < 1024 && onClose()}
                    >
                      <item.icon className="w-4 h-4 flex-shrink-0" />
                      <span>{item.name}</span>
                    </NavLink>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Support */}
          <a
            href="#"
            className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-gray-600 hover:bg-gray-50 hover:text-gray-900 transition-colors ${
              collapsed ? 'justify-center' : ''
            }`}
          >
            <HelpCircle className="w-5 h-5 flex-shrink-0" />
            {!collapsed && <span className="text-sm">Support</span>}
          </a>
        </div>
      </aside>

      {/* Sidebar Mobile */}
      <aside className={`fixed left-0 top-0 h-screen bg-white/95 backdrop-blur-xl z-50 w-64 flex flex-col transform transition-transform duration-300 shadow-xl lg:hidden ${
        open ? 'translate-x-0' : '-translate-x-full'
      }`}>

        {/* Header avec Logo */}
        <div className="h-16 flex items-center justify-between px-4 border-b border-gray-100">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary-500 to-secondary-500 flex items-center justify-center text-white font-heading font-bold text-lg shadow-md">
              O
            </div>
            <span className="font-heading font-semibold text-gray-900">Orbloom</span>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
          >
            <ChevronLeft className="w-5 h-5 text-gray-600" />
          </button>
        </div>

        {/* Navigation Mobile */}
        <nav className="flex-1 overflow-y-auto py-6 px-3">
          {/* Section Principale */}
          <div className="space-y-1">
            <div className="px-3 mb-2">
              <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Principal</span>
            </div>
            {filterNavigation(navigation.principal).map((item) => (
              <NavLink
                key={item.name}
                to={item.href}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all ${
                    isActive
                      ? 'bg-primary-50 text-primary-600 font-medium shadow-sm'
                      : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                  }`
                }
                onClick={onClose}
              >
                <item.icon className="w-5 h-5 flex-shrink-0" />
                <span className="text-sm">{item.name}</span>
              </NavLink>
            ))}
          </div>
        </nav>

        {/* Footer Mobile avec Paramètres et Support */}
        <div className="p-3 border-t border-gray-100 space-y-2">
          {/* Section Administration Mobile (pour les admins) */}
          {hasRole(['responsable', 'super_admin']) && (
            <div className="mb-4">
              <div className="px-3 mb-2">
                <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Administration</span>
              </div>
              <div className="space-y-1">
                {filterNavigation(navigation.settings).map((item) => (
                  <NavLink
                    key={item.name}
                    to={item.href}
                    className={({ isActive }) =>
                      `flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors ${
                        isActive
                          ? 'bg-gray-100 text-gray-900 font-medium'
                          : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                      }`
                    }
                    onClick={onClose}
                  >
                    <item.icon className="w-4 h-4 flex-shrink-0" />
                    <span>{item.name}</span>
                  </NavLink>
                ))}
              </div>
            </div>
          )}

          {/* Support */}
          <a
            href="#"
            className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-gray-600 hover:bg-gray-50 hover:text-gray-900 transition-colors"
          >
            <HelpCircle className="w-5 h-5 flex-shrink-0" />
            <span className="text-sm">Support</span>
          </a>
        </div>
      </aside>
    </>
  )
}
